import { useEffect, useState, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage, DEFAULT_LOCALE } from '@/hooks/useLanguage';
import NotFound from './NotFound';

// Lazy: schwere Pages (framer-motion, date-fns, dompurify) raus aus Initial-Bundle
const CategoryPage = lazy(() => import('./CategoryPage'));
const BlogPostPage = lazy(() => import('./BlogPostPage'));
const BlogCategoryPage = lazy(() => import('./BlogCategoryPage'));

type SlugType = 'product-category' | 'blog-category' | 'blog-post' | 'not-found';

export default function SlugResolver() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { locale } = useLanguage();
  const [resolved, setResolved] = useState<SlugType | null>(null);

  useEffect(() => {
    if (!slug) {
      setResolved('not-found');
      return;
    }

    let active = true;

    const resolve = async () => {
      setResolved(null);

      const [redirectRes, catRes, blogCatRes, postRes, catTransRes, postTransRes] = await Promise.all([
        supabase
          .from('redirects')
          .select('target_path, status_code')
          .eq('source_path', `/${slug}`)
          .eq('is_active', true)
          .maybeSingle(),
        supabase
          .from('categories')
          .select('id')
          .eq('slug', slug)
          .maybeSingle(),
        supabase
          .from('blog_categories')
          .select('id')
          .eq('slug', slug)
          .maybeSingle(),
        supabase
          .from('blog_posts')
          .select('id')
          .eq('slug', slug)
          .maybeSingle(),
        supabase
          .from('category_translations')
          .select('category_id')
          .eq('slug', slug)
          .limit(1)
          .maybeSingle(),
        supabase
          .from('blog_post_translations')
          .select('post_id')
          .eq('slug', slug)
          .limit(1)
          .maybeSingle(),
      ]);

      if (!active) return;

      // Priority: redirect > category > blog category > blog post > translated > not found
      if (redirectRes.data) {
        navigate(redirectRes.data.target_path, { replace: true });
        return;
      }
      if (catRes.data) { setResolved('product-category'); return; }
      if (catTransRes?.data) { setResolved('product-category'); return; }
      if (blogCatRes.data) { setResolved('blog-category'); return; }
      if (postRes.data) { setResolved('blog-post'); return; }
      if (postTransRes?.data) { setResolved('blog-post'); return; }

      setResolved('not-found');
    };

    resolve();

    return () => { active = false; };
  }, [slug, navigate, locale]);

  if (resolved === null) {
    return <div className="min-h-screen" />;
  }

  const fallback = <div className="min-h-screen" />;
  switch (resolved) {
    case 'product-category':
      return <Suspense fallback={fallback}><CategoryPage /></Suspense>;
    case 'blog-category':
      return <Suspense fallback={fallback}><BlogCategoryPage /></Suspense>;
    case 'blog-post':
      return <Suspense fallback={fallback}><BlogPostPage /></Suspense>;
    default:
      return <NotFound />;
  }
}
